import type { EvaluationStep, StepTemplateEntry } from "@/hooks/types";

/**
 * Declared dependencies between evaluation steps (PRD D6/D7, OQ-28 resolved as
 * C on 2026-08-11) — the two halves of the same idea, in the two shapes it
 * takes:
 *
 * * on a **template**, an entry waits for other entries by their stable `key`,
 *   and this module is what the editor uses to keep that graph honest (no
 *   cycles, no reference to an entry that is gone);
 * * on a **request**, a snapshot step waits for step ids the backend resolved
 *   at instantiation, and this module derives whether it has started.
 *
 * "Not started" is derived, never stored. `EvaluationStepStatus` stays
 * PENDING | DONE | SKIPPED — the closed set `firestore.rules` pins on a step
 * update — so a fourth value would have cost a rules change, a migration of
 * every running evaluation and a decision about who may write it, all to record
 * something the graph already implies. The price is that every reader computes
 * it, which is why they all come here. The backend derives the same thing in
 * `navigator/domain/service/step_dependencies.py`; the two must agree.
 *
 * A **SKIPPED dependency releases its dependants**, exactly as a DONE one does:
 * ADR 0010 calls a skip "this step did not apply here", so a chain stuck behind
 * one would be a stall the BU admin could only clear by unpicking the template.
 * The editor says so where the dependency is declared.
 *
 * `order` is not consulted anywhere in this file, and must not be: it is a
 * display key, and inferring a dependency from list position is the thing
 * OQ-28 forbids.
 */

/** Whether the step's declared dependencies are all closed.
 *
 *  A step with no declaration has always started. A dependency naming a step
 *  the request does not have counts as satisfied — the backend's resolution
 *  cannot write one, and treating it as unsatisfied would turn a bad id into a
 *  step nobody can ever release. */
export function hasStarted(
  step: EvaluationStep,
  stepsById: ReadonlyMap<string, EvaluationStep>,
): boolean {
  return blockingSteps(step, stepsById).length === 0;
}

/** What to show a *reader* as the reason a step has not started, or nothing.
 *
 *  `blockingSteps` answers a question about the graph; this answers a question
 *  about the screen, and the two differ on one case: a step somebody has
 *  already closed. The BU admin may tick or skip a step whose dependencies are
 *  still open — the rules allow it and their request detail is where that
 *  escape hatch lives — and afterwards the step is done, whatever the graph
 *  says. Rendering "not started" beside a green check (or beneath a
 *  strikethrough "Skipped") states both at once, and the surface that provokes
 *  it most is the one whose whole purpose is acting out of order (#58 review).
 *
 *  Every surface that draws a not-started state uses this; nothing calls
 *  `blockingSteps` directly for display. */
export function notStartedBlockers(
  step: EvaluationStep,
  stepsById: ReadonlyMap<string, EvaluationStep>,
): EvaluationStep[] {
  if (step.status !== "PENDING") return [];
  return blockingSteps(step, stepsById);
}

/** The steps this one is still waiting for — the graph's answer, used to
 *  decide whether a step has started. Only open ones: a DONE or SKIPPED
 *  dependency is no longer blocking anything, and listing it would read as if
 *  it were. For anything user-facing, use `notStartedBlockers`. */
export function blockingSteps(
  step: EvaluationStep,
  stepsById: ReadonlyMap<string, EvaluationStep>,
): EvaluationStep[] {
  return (step.dependsOn ?? [])
    .map((id) => stepsById.get(id))
    .filter(
      (dependency): dependency is EvaluationStep =>
        dependency !== undefined && dependency.status === "PENDING",
    );
}

/** Index a request's steps for the lookups above. */
export function byId(
  steps: readonly EvaluationStep[],
): ReadonlyMap<string, EvaluationStep> {
  return new Map(steps.map((step) => [step.id, step]));
}

/** The steps of one request that have started, in the given order. */
export function startedSteps(
  steps: readonly EvaluationStep[],
): EvaluationStep[] {
  const index = byId(steps);
  return steps.filter((step) => hasStarted(step, index));
}

/** Every step that is open **and** started: the work actually with someone
 *  right now. This is what the requester's "waiting on" line names and what
 *  the D7 sweep chases — a step still waiting for another has not reached
 *  anybody, so naming it there would overstate what is in flight. */
export function activeSteps(
  steps: readonly EvaluationStep[],
): EvaluationStep[] {
  return startedSteps(steps).filter((step) => step.status === "PENDING");
}

// ─── Template side ────────────────────────────────────────────────────────

/** A fresh entry key. Random rather than derived from name or position: both
 *  of those change, and a key that changes is not a key. */
export function newEntryKey(): string {
  return crypto.randomUUID();
}

/** The entries with a key on every one of them.
 *
 *  Templates predate keys, so the editor backfills on the next save — an entry
 *  without a key instantiates fine but cannot be named as a dependency, and
 *  the picker has nothing to offer until it has one. Entries that already have
 *  a key keep it: it is what existing declarations point at. */
export function withKeys(
  entries: readonly StepTemplateEntry[],
): StepTemplateEntry[] {
  return entries.map((entry) =>
    entry.key ? entry : { ...entry, key: newEntryKey() },
  );
}

/** Drop every reference to `key` from the other entries.
 *
 *  Deleting a step must not leave a dependency on something that no longer
 *  exists — the template would carry a reference nothing can satisfy, which
 *  the backend then drops silently at instantiation. Removing it here is what
 *  makes the editor's refusal of unknown references true after a delete. */
export function dropReferences(
  entries: readonly StepTemplateEntry[],
  key: string | undefined,
): StepTemplateEntry[] {
  if (!key) return [...entries];
  return entries.map((entry) =>
    entry.dependsOn?.includes(key)
      ? { ...entry, dependsOn: entry.dependsOn.filter((k) => k !== key) }
      : entry,
  );
}

/** Whether declaring `dependsOn` on the entry at `index` would create a cycle
 *  — a set of steps each waiting for the next, which nothing can ever start.
 *
 *  Checked against the template as it would be *after* the edit, so it catches
 *  both directions: naming a step that already waits for this one, and naming
 *  this one from a step it waits for. A self-reference is the shortest cycle
 *  and is caught by the same walk. */
export function wouldCycle(
  entries: readonly StepTemplateEntry[],
  index: number,
  dependsOn: readonly string[],
): boolean {
  const next = entries.map((entry, i) =>
    i === index ? { ...entry, dependsOn: [...dependsOn] } : entry,
  );
  const byKey = new Map(
    next
      .filter((entry) => entry.key)
      .map((entry) => [entry.key as string, entry]),
  );
  const edited = next[index];
  // The caller holds an index captured when its dialog opened, against a live
  // snapshot; `eligibleDependencies` re-runs this on every render, so an entry
  // deleted elsewhere would throw during render and take the whole route with
  // it — not merely the click (#58 review, round 2). No entry means no cycle
  // to create; the save path's own guard produces the "step was removed"
  // toast when the admin gets there.
  if (!edited) return false;
  // Walk everything reachable from the edited entry; arriving back at it means
  // it waits, transitively, for itself.
  const seen = new Set<string>();
  const queue = [...dependsOn];
  while (queue.length > 0) {
    const key = queue.shift() as string;
    if (edited.key && key === edited.key) return true;
    if (seen.has(key)) continue;
    seen.add(key);
    queue.push(...(byKey.get(key)?.dependsOn ?? []));
  }
  return false;
}

/** The entries that may be offered as dependencies of the entry at `index`:
 *  everything else that has a key and would not close a loop. `index` is null
 *  when adding, where nothing can point back yet. */
export function eligibleDependencies(
  entries: readonly StepTemplateEntry[],
  index: number | null,
): StepTemplateEntry[] {
  return entries.filter((entry, i) => {
    if (i === index || !entry.key) return false;
    if (index === null) return true;
    return !wouldCycle(entries, index, [entry.key]);
  });
}

/** Names of the entries an entry waits for, in template order, for display.
 *  A key with no entry is skipped rather than rendered as a dangling id. */
export function dependencyNames(
  entries: readonly StepTemplateEntry[],
  entry: StepTemplateEntry,
): string[] {
  const declared = new Set(entry.dependsOn ?? []);
  return entries
    .filter((candidate) => candidate.key && declared.has(candidate.key))
    .map((candidate) => candidate.name);
}

/** One heading's worth of the template: the steps that begin at the same
 *  point, because they wait for exactly the same thing. */
export interface TemplateGroup {
  /** Keys of the entries every member waits for, sorted — the group's
   *  identity, and the only collision-free thing to key a rendered section on.
   *  Names cannot serve: two steps may share one, and then two genuinely
   *  different groups look like the same one (#58 review). */
  dependsOn: string[];
  /** Names of the entries every member waits for, in template order. Empty on
   *  the first group — those steps begin as soon as the proposal arrives. */
  after: string[];
  /** The members, carrying their index in the template list, so the editor's
   *  edit/delete/move handlers still address the array they came from. */
  members: Array<{ entry: StepTemplateEntry; index: number }>;
}

/**
 * The template flattened into the order the editor shows it in: group by
 * group, members in their own order within each.
 *
 * Applied on every save, so the stored array **is** the display order. That
 * keeps `order` — assigned from this array's positions when a request is
 * instantiated — meaningful on the surfaces that render by it: the requester's
 * list and the BU admin's request detail sort by `order`, and without this they
 * would show a step above the very step it waits for, while the editor showed
 * the opposite (#58 review, round 3). The grouping puts every dependency at a
 * lower depth than its dependants, so the result is always a topological order:
 * a step can never be listed above something it waits for.
 *
 * Idempotent — sorting an already-sorted template is a no-op, so it does not
 * churn the doc on repeated saves.
 */
export function inGroupOrder(
  entries: readonly StepTemplateEntry[],
): StepTemplateEntry[] {
  return templateGroups(entries).flatMap((group) =>
    group.members.map((member) => member.entry),
  );
}

/**
 * The template as the process it describes: steps grouped by what they wait
 * for, the groups ordered so nothing appears before what it depends on.
 *
 * Grouped by the **exact dependency set**, not by how deep in the chain a step
 * sits. Two steps that both begin "after legal" belong together; a step that
 * waits for legal *and* finance begins somewhere else and gets its own
 * heading, even though it is the same distance down the graph — a shared
 * heading there would claim each member waits for the union of both, which is
 * not what any of them declared.
 *
 * This derives *presentation* from the declarations, which is the opposite of
 * the thing OQ-28 forbids: no dependency is ever inferred from where a step
 * sits in the list. List position survives only within a group, where it is
 * the display order the admin controls.
 */
export function templateGroups(
  entries: readonly StepTemplateEntry[],
): TemplateGroup[] {
  const byKey = new Map(
    entries
      .filter((entry) => entry.key)
      .map((entry) => [entry.key as string, entry]),
  );
  // Only references that resolve: a dangling one is dropped when the request
  // is instantiated, so grouping by it would show the admin an order that is
  // not going to happen.
  const declaredOf = (entry: StepTemplateEntry) =>
    (entry.dependsOn ?? []).filter((key) => byKey.has(key));
  const depthCache = new Map<string, number>();
  const depthOf = (entry: StepTemplateEntry, visiting: Set<string>): number => {
    const key = entry.key;
    if (key !== undefined) {
      const cached = depthCache.get(key);
      if (cached !== undefined) return cached;
      // Cycle guard. The editor refuses to create one, and instantiation
      // breaks any that arrives anyway — this only keeps the editor from
      // recursing forever while looking at one.
      if (visiting.has(key)) return 0;
      visiting.add(key);
    }
    const dependencies = declaredOf(entry).map(
      (dependency) => byKey.get(dependency) as StepTemplateEntry,
    );
    const depth =
      dependencies.length === 0
        ? 0
        : 1 +
          Math.max(
            ...dependencies.map((dependency) => depthOf(dependency, visiting)),
          );
    if (key !== undefined) {
      visiting.delete(key);
      depthCache.set(key, depth);
    }
    return depth;
  };

  const groups = new Map<
    string,
    TemplateGroup & { depth: number; firstIndex: number }
  >();
  entries.forEach((entry, index) => {
    const declared = declaredOf(entry);
    const groupKey = [...declared].sort().join(" ");
    const existing = groups.get(groupKey);
    if (existing) {
      existing.members.push({ entry, index });
      return;
    }
    const declaredSet = new Set(declared);
    groups.set(groupKey, {
      dependsOn: [...declared].sort(),
      after: entries
        .filter((candidate) => candidate.key && declaredSet.has(candidate.key))
        .map((candidate) => candidate.name),
      members: [{ entry, index }],
      depth: depthOf(entry, new Set()),
      firstIndex: index,
    });
  });

  return [...groups.values()]
    .sort((a, b) => a.depth - b.depth || a.firstIndex - b.firstIndex)
    .map(({ dependsOn, after, members }) => ({ dependsOn, after, members }));
}
